
import { useState, FormEvent } from "react";
import { useMarketingLang } from "../i18n.jsx";

type Status = "idle" | "submitting" | "success" | "error";

const LEADS_URL =
  (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_URL || "") +
  "/api/v1/leads";

export default function CTA() {
  const { t } = useMarketingLang();
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [project, setProject] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string>("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      const message = [
        company ? `Company: ${company}` : "",
        project ? `Project: ${project}` : "",
      ].filter(Boolean).join("\n");
      const resp = await fetch(LEADS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, message, source: "pricing-cta" }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(
        err instanceof Error ? err.message : "Submission failed — please email us directly.",
      );
    }
  }

  return (
    <section
      id="book"
      className="relative section-y overflow-hidden border-t-[0.5px] border-white/[0.06]"
      style={{
        background:
          "radial-gradient(900px 500px at 50% 0%, rgba(77,212,232,0.14), transparent 60%)",
      }}
    >
      <div className="container-page text-center">
        <p className="eyebrow mb-6">{t('cta.eyebrow')}</p>
        <h2 className="h-section text-ink max-w-[680px] mx-auto">
          {t('cta.title')}
        </h2>

        <div className="mt-10 mx-auto max-w-[460px]">
          {status === "success" ? (
            <div className="rounded-2xl p-8 bg-bg-card border-hairline-base text-left">
              <p
                className="font-mono text-[11px] uppercase text-teal mb-3"
                style={{ letterSpacing: "0.21em" }}
              >
                {t('cta.requestReceived')}
              </p>
              <p className="text-[16px] text-ink leading-[1.55]">
                {t('cta.thankYou')}
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl p-7 bg-bg-card border-hairline-base text-left flex flex-col gap-5"
            >
              <Field label={t('cta.workEmail')}>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="navn@selskap.no"
                  className="w-full bg-transparent text-[15px] text-ink placeholder:text-white/30 outline-none border-b-[0.5px] border-white/15 focus:border-teal py-2 transition-colors"
                />
              </Field>

              <Field label={t('cta.company')}>
                <input
                  type="text"
                  required
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder={t('cta.companyPlaceholder')}
                  className="w-full bg-transparent text-[15px] text-ink placeholder:text-white/30 outline-none border-b-[0.5px] border-white/15 focus:border-teal py-2 transition-colors"
                />
              </Field>

              <Field label={t('cta.project')}>
                <textarea
                  value={project}
                  onChange={(e) => setProject(e.target.value)}
                  placeholder={t('cta.projectPlaceholder')}
                  rows={2}
                  className="w-full bg-transparent text-[15px] text-ink placeholder:text-white/30 outline-none border-b-[0.5px] border-white/15 focus:border-teal py-2 transition-colors resize-none"
                />
              </Field>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="group inline-flex items-center justify-center gap-2 mt-2 px-6 py-3.5 rounded-full bg-teal text-bg-base text-[15px] font-medium hover:brightness-110 transition-all disabled:opacity-60"
              >
                {status === "submitting" ? t('cta.sending') : t('cta.requestDemo')}
                <span className="inline-block transition-transform group-hover:translate-x-[3px]">
                  →
                </span>
              </button>
              {status === "error" && (
                <p className="text-[12px] text-danger mt-1">
                  {t('cta.submitFailed')} ({error}). Mail{" "}
                  <a href="mailto:hello@seidr.site" className="underline">hello@seidr.site</a> {t('cta.mailInstead')}.
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span
        className="font-mono text-[10.5px] uppercase text-white/55"
        style={{ letterSpacing: "0.21em" }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
